import { useRef } from 'react';
import Select, { ActionMeta, SingleValue } from 'react-select';
import { hasValue, isNullOrUndefined } from '@r-and-a-shared-ui/utils';
import { FormFieldProps, OptionProps } from '../FormField/FormField';
import { useController, useFormContext } from 'react-hook-form';
import { defaultValidationMessage } from '../config/validators/validators';
import { replaceSpecialLabelValue } from './helpers/country-autofill-helper';
import {
  ID,
  NAME,
  NUMERIC_ISO,
  COUNTRY_ID,
} from '@r-and-a-shared-ui/common-forms-ui';

import styles from './FormDropdownPP.module.scss';

export interface CountryOptionProps extends OptionProps {
  id?: string;
  name?: string;
  isoCode2?: string;
  isoCode3?: string;
  countryId?: string;
  numericIso?: string;
}
export interface PPFormFieldProps extends FormFieldProps {
  logicalName?: string;
  otherOptionValue?: string;
  uniqueValueSelection?: boolean;
  disabledOptionsValues?: string[];
  hiddenClearable?: boolean;
  options?: OptionProps[] | CountryOptionProps[];
}

export const FormDropdownPP = (props: PPFormFieldProps) => {
  const selectRef = useRef<any | null>(null);
  const {
    name,
    fieldRef,
    placeholder,
    label,
    isDisabled,
    validationMessage,
    options,
    triggerError,
    entireObjectSelection,
    menuPlacement,
    hiddenClearable,
    logicalName,
    disabledOptionsValues,
  } = props;
  const {
    register,
    formState: { errors },
    control,
    setValue,
  } = useFormContext();

  const { field } = useController({ name, control });
  const {
    value: selectedOption,
    onChange: selectOnChange,
    ...restField
  } = field;

  const { ref, ...rest } = register(name);

  const getValue = () => {
    let selectionValue = selectedOption;
    if (entireObjectSelection) {
      selectionValue = selectedOption?.id || selectedOption;
    }
    const foundValue =
      options?.find(option => option?.value === selectionValue) ?? null;

    return foundValue;
  };

  const onSelectChange = (
    option: SingleValue<OptionProps & CountryOptionProps>,
    { action }: ActionMeta<OptionProps & CountryOptionProps>,
  ) => {
    if (action === 'clear') {
      selectOnChange('');
      return;
    }

    let objectToSelect:
      | string
      | { id?: string; name?: string; numericIso?: string; countryId?: string }
      | undefined = option?.value;

    if (entireObjectSelection) {
      objectToSelect = {
        id: option?.value,
        name: option?.label,
      };
      setValue(`${name}_${ID}`, option?.value || '', { shouldDirty: true });
      setValue(`${name}_${NAME}`, option?.label || '', { shouldDirty: true });
      if (logicalName) {
        (objectToSelect as any).logicalName = logicalName;
      }
      if (option?.numericIso) {
        (objectToSelect as any).numericIso = option.numericIso;
        setValue(`${name}_${NUMERIC_ISO}`, option?.numericIso || '', {
          shouldDirty: true,
        });
      }
      if (option?.countryId) {
        (objectToSelect as any).countryId = option.countryId;
        setValue(`${name}_${COUNTRY_ID}`, option?.countryId || '', {
          shouldDirty: true,
        });
      }
    }

    selectOnChange(objectToSelect);
  };

  const disableOptions = (option: OptionProps) => {
    if (
      !disabledOptionsValues ||
      disabledOptionsValues?.length === 0 ||
      isNullOrUndefined(option?.value)
    ) {
      return false;
    }
    if (disabledOptionsValues.includes(option.value)) {
      return true;
    }
    return false;
  };

  const addErrorClass = triggerError || !!errors?.[name];
  return (
    <div
      ref={fieldRef}
      className={`${styles['form-element']} ${styles['form-select']} ${
        addErrorClass ? styles['form-element__has-error'] : ''
      }`}
    >
      {hasValue(label) && (
        <label
          className={`${styles['form-element__label']} ${
            getValue() ? styles['form-element__has-value'] : ''
          }`}
          htmlFor={name}
        >
          {replaceSpecialLabelValue(label)}
        </label>
      )}
      <Select
        id={name}
        classNamePrefix={'custom__select'}
        placeholder={replaceSpecialLabelValue(placeholder || label)}
        isClearable={!hiddenClearable}
        options={options || []}
        isOptionDisabled={disableOptions}
        value={getValue()}
        {...restField}
        {...rest}
        ref={e => {
          ref(e);
          selectRef.current = e;
        }}
        onChange={onSelectChange}
        isDisabled={isDisabled}
        menuPlacement={menuPlacement}
      />
      <div className={styles['form-element__bar']}></div>
      {errors?.[name] && (
        <small className={styles['form-element__hint']}>
          {validationMessage || defaultValidationMessage}
        </small>
      )}
    </div>
  );
};

export default FormDropdownPP;
